import spotlight from '../../util/discoverSpotlight.cjs';

const { normalizeSpotlightBooks } = spotlight;
const datePattern = /^\d{4}-\d{2}-\d{2}$/;

const parseDate = value => {
  if (typeof value !== 'string' || !datePattern.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? date
    : null;
};

const isActiveWindow = (validFrom, expiresAt, now) => {
  const start = parseDate(validFrom);
  const end = parseDate(expiresAt);
  return Boolean(start && end && start <= now && now < end);
};

const getMarkdownPayload = pageAsset => {
  const section = pageAsset?.sections?.find(
    item => item.sectionId === 'discover-editorial-data' && item.sectionType === 'article'
  );
  const block = section?.blocks?.find(item => item.blockType === 'defaultBlock');
  const content = block?.text?.fieldType === 'markdown' ? block.text.content : '';
  if (typeof content !== 'string') return null;

  const codeBlock = content.match(/```(?:json)?\s*([\s\S]*?)```/i);
  return (codeBlock ? codeBlock[1] : content).trim();
};

const eligibleBooks = (books, now, { future = false } = {}) =>
  (Array.isArray(books) ? books : []).filter(book => {
    const releaseDate = parseDate(book?.releaseDate);
    const verifiedAt = parseDate(book?.metadataVerifiedAt);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (
      !releaseDate ||
      !verifiedAt ||
      verifiedAt > today ||
      book.releaseDateVerified !== true ||
      typeof book.metadataSource !== 'string' ||
      !book.metadataSource.trim() ||
      typeof book.sourceUrl !== 'string' ||
      !book.sourceUrl.trim()
    ) {
      return false;
    }

    if (future) {
      const futureLimit = new Date(today);
      futureLimit.setDate(futureLimit.getDate() + 90);
      return releaseDate > today && releaseDate <= futureLimit;
    }

    const cutoff = new Date(today);
    cutoff.setDate(cutoff.getDate() - 30);
    return releaseDate <= today && releaseDate >= cutoff;
  });

const normalizeEditorialBook = book => ({
  ...book,
  note: book.note || book.editorialNote || book.supportingLine || '',
  cover: {
    ...book.cover,
    resolvedUrl: book.coverUrl || book.cover?.resolvedUrl || null,
  },
  sources: book.sourceUrl ? [{ url: book.sourceUrl }] : book.sources || [],
});

const activeSection = (sections, key, now) => {
  const section = sections?.[key];
  return section && isActiveWindow(section.validFrom, section.expiresAt, now) ? section : null;
};

/**
 * Parse PageBuilder-supported markdown content and expose only currently valid editorial data.
 * The payload is JSON inside the markdown field of the discover-editorial-data article section.
 *
 * @param {Object} pageAsset Sharetribe Page asset data
 * @param {Date} now Current time, injectable for deterministic tests
 * @returns {Object|null} Validated and normalized editorial content
 */
export const parseDiscoverEditorial = (pageAsset, now = new Date()) => {
  const markdown = getMarkdownPayload(pageAsset);
  if (!markdown) return null;

  let issue;
  try {
    issue = JSON.parse(markdown);
  } catch (error) {
    console.error('Discover editorial content is not valid JSON.', error);
    return null;
  }

  if (
    !issue ||
    typeof issue !== 'object' ||
    !parseDate(issue.issueDate) ||
    parseDate(issue.issueDate) > now ||
    !issue.sections ||
    typeof issue.sections !== 'object' ||
    Array.isArray(issue.sections) ||
    !isActiveWindow(issue.validFrom, issue.expiresAt, now)
  ) {
    return null;
  }

  const sections = issue.sections;
  const shelfEdit = activeSection(sections, 'shelfEdit', now);
  const moment = activeSection(sections, 'booksHavingAMoment', now);
  const newReleases = activeSection(sections, 'newReleases', now);
  const closerToHome = activeSection(sections, 'closerToHome', now);
  const authorSpotlight = activeSection(sections, 'authorSpotlight', now);
  const comingSoon = activeSection(sections, 'comingSoon', now);

  return {
    issueDate: issue.issueDate,
    validFrom: issue.validFrom,
    expiresAt: issue.expiresAt,
    backInSpotlight: normalizeSpotlightBooks(activeSection(sections, 'backInSpotlight', now)),
    heroFeatures:
      shelfEdit && moment
        ? [
            {
              ...moment,
              eyebrow: moment.eyebrow || shelfEdit.title || 'The Shelf Edit',
              title: moment.title || shelfEdit.title,
              description: moment.description || shelfEdit.description,
              ctaLabel: shelfEdit.ctaLabel || 'Explore the edit',
              ctaRoute: 'TrendingPage',
              ctaParams: { page: 'curated-reads' },
              books: Array.isArray(moment.books) ? moment.books.map(normalizeEditorialBook) : [],
            },
          ]
        : [],
    newThisMonth: newReleases
      ? eligibleBooks(newReleases.books, now).map(book => ({
          ...normalizeEditorialBook(book),
          editorialNote: book.editorialNote || book.supportingLine || '',
          genre: book.genre || '',
        }))
      : [],
    comingNextMonth: comingSoon
      ? eligibleBooks(comingSoon.books, now, { future: true }).map(book => ({
          ...normalizeEditorialBook(book),
          note: book.editorialNote || book.supportingLine || '',
        }))
      : [],
    canadianShelf: Array.isArray(closerToHome?.books)
      ? closerToHome.books.map(normalizeEditorialBook)
      : [],
    authorSpotlight: authorSpotlight
      ? {
          ...authorSpotlight,
          books: Array.isArray(authorSpotlight.books)
            ? authorSpotlight.books.map(normalizeEditorialBook)
            : [],
        }
      : null,
  };
};

export const isDiscoverDateActive = isActiveWindow;
export const isValidDiscoverDate = value => Boolean(parseDate(value));
